import React, { createContext, useContext, ReactNode, useState, useRef, useCallback, useEffect } from 'react';

// Definir la interfaz para los mensajes WebSocket
interface WebSocketMessage {
  type: string;
  data: unknown;
  timestamp: string;
}

// Definir la interfaz para las opciones del WebSocket
interface WebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  reconnectDelay?: number;
  maxReconnectAttempts?: number;
}

// Definir la interfaz para el contexto WebSocket
interface WebSocketContextType {
  isConnected: boolean;
  connectionId: string | null;
  send: (data: unknown) => void;
  disconnect: () => void;
  reconnect: () => void;
  reconnectAttempts: number;
}

// Crear el contexto
const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Props para el provider
interface WebSocketProviderProps {
  children: ReactNode;
  options?: WebSocketOptions;
}

// Provider del contexto
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  options = {},
}) => {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    reconnectDelay = 3000,
    maxReconnectAttempts = 5,
  } = options;

  // Estados del WebSocket
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Referencias para mantener la conexión
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnect = useRef(true);
  const isConnectingRef = useRef(false);

  // Obtener la URL del WebSocket desde las variables de entorno
  const websocketUrl = import.meta.env.VITE_WEBSOCKET_URL || '';
  

  // Función para conectar al WebSocket
  const connect = useCallback(() => {
    // Evitar múltiples conexiones simultáneas
    if (wsRef.current?.readyState === WebSocket.OPEN || isConnectingRef.current) {
      return;
    }

    if (!websocketUrl) {
      console.error('WebSocket URL no está configurada');
      return;
    }

    isConnectingRef.current = true;
    
    try {
      const ws = new WebSocket(websocketUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        isConnectingRef.current = false;
        setIsConnected(true);
        setReconnectAttempts(0);
        onConnect?.();
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          
          if (message.type === 'connection' && typeof message.data === 'object' && message.data !== null) {
            const data = message.data as { connectionId?: string };
            if (data.connectionId) {
              setConnectionId(data.connectionId);
            }
          }

          onMessage?.(message);
        } catch (error) {
          console.error('Error al parsear mensaje WebSocket:', error);
        }
      };

      ws.onerror = (event) => {
        isConnectingRef.current = false;
        console.error('WebSocket error:', event);
        onError?.(event);
      };

      ws.onclose = (event) => {
        isConnectingRef.current = false;
        
        setIsConnected(false);
        setConnectionId(null);
        wsRef.current = null;
        onDisconnect?.();

        // Solo intentar reconectar si no fue una desconexión limpia y tenemos intentos disponibles
        if (shouldReconnect.current && reconnectAttempts < maxReconnectAttempts && !event.wasClean) {
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts((prev) => prev + 1);
            connect();
          }, reconnectDelay);
        }
      };
    } catch (error) {
      isConnectingRef.current = false;
      console.error('Error al crear conexión WebSocket:', error);
    }
  }, [websocketUrl, onConnect, onMessage, onError, onDisconnect, reconnectDelay, maxReconnectAttempts, reconnectAttempts]);

  // Función para desconectar
  const disconnect = useCallback(() => {
    shouldReconnect.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Función para reconectar
  const reconnect = useCallback(() => {
    disconnect();
    shouldReconnect.current = true;
    setReconnectAttempts(0);
    connect();
  }, [disconnect, connect]);

  // Función para enviar mensajes
  const send = useCallback((data: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  // Efecto para manejar la conexión inicial
  useEffect(() => {
    // Agregar un pequeño delay antes de conectar para asegurar que la app esté lista
    const initialDelay = setTimeout(() => {
      // Solo conectar si no hay una conexión activa
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        connect();
      }
    }, 1000); // 1 segundo de delay inicial

    return () => {
      clearTimeout(initialDelay);
      shouldReconnect.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [websocketUrl, connect]); // Dependemos de la URL y la función connect

  // Valores del contexto
  const contextValue: WebSocketContextType = {
    isConnected,
    connectionId,
    send,
    disconnect,
    reconnect,
    reconnectAttempts,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};
